import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDashboard } from '../hooks/useCRM';
import '../crm.css';
import { activitiesPath, crmBasePath, leadsPath } from '../utils/crmEnv';

// Currency formatter helper
const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    const num = Number(value);
    if (num >= 10000000) {
        return `₹${(num / 10000000).toFixed(1)}Cr`;
    }
    if (num >= 100000) {
        return `₹${(num / 100000).toFixed(1)}L`;
    }
    if (num >= 1000) {
        return `₹${(num / 1000).toFixed(1)}K`;
    }
    return `₹${num}`;
};

const CRMDashboard = () => {
    const history = useHistory();
    const [range, setRange] = useState('30d');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const { stats, loading, error, updateParams, refetch } = useDashboard({ range: '30d' });

    useEffect(() => {
        if (range !== 'custom') {
            updateParams({ range });
            return;
        }
        if (customFrom && customTo) {
            updateParams({ range: 'custom', from: customFrom, to: customTo });
        }
    }, [range, customFrom, customTo, updateParams]);

    // Error state
    if (error) {
        return (
            <div className="crm-container">
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: 'var(--crm-danger)'
                }}>
                    <h3 style={{ margin: '0 0 16px', color: 'var(--crm-gray-900)' }}>
                        Error Loading Dashboard
                    </h3>
                    <p style={{ margin: '0 0 20px', color: 'var(--crm-gray-500)' }}>
                        {error}
                    </p>
                    <button
                        className="crm-btn crm-btn-primary"
                        onClick={refetch}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const sparklineValues = (series = []) => {
        if (!series.length) return [];
        const values = series.map((entry) => entry.count ?? entry);
        const max = Math.max(...values, 1);
        const normalized = values.map((value) => Math.round((value / max) * 10));
        return normalized.length > 10 ? normalized.slice(-10) : normalized;
    };

    const leadFunnel = stats?.funnel || [];
    const sourcePerformance = (stats?.sources || []).slice().sort((a, b) => b.converted - a.converted);
    const totalSourceLeads = sourcePerformance.reduce((sum, row) => sum + (Number(row?.leads) || 0), 0);
    const timeSeries = stats?.timeSeries || {};
    const qualifiedStage = leadFunnel.find((stage) => stage.stage === 'qualified');
    const convertedStage = leadFunnel.find((stage) => stage.stage === 'converted');
    const closedDealsCount = (stats?.deals?.won || 0) + (stats?.deals?.lost || 0);

    if (loading) {
        return (
            <div className="crm-container">
                <div className="crm-dashboard-skeleton">
                    <div className="crm-skeleton-header"></div>
                    <div className="crm-skeleton-kpis"></div>
                    <div className="crm-skeleton-block"></div>
                    <div className="crm-skeleton-block"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="crm-container">
            {/* Page Header */}
            <div className="crm-page-header">
                <h1 className="crm-page-title">CRM Dashboard</h1>
                <div className="crm-page-actions">
                    <div className="crm-range-switcher">
                        {['7d', '30d', '90d'].map((value) => (
                            <button
                                key={value}
                                className={`crm-btn crm-btn-sm ${range === value ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                                onClick={() => setRange(value)}
                            >
                                {value.replace('d', 'D')}
                            </button>
                        ))}
                        <button
                            className={`crm-btn crm-btn-sm ${range === 'custom' ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                            onClick={() => setRange('custom')}
                        >
                            Custom
                        </button>
                        {range === 'custom' && (
                            <div className="crm-range-custom">
                                <input
                                    type="date"
                                    className="crm-input"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                />
                                <input
                                    type="date"
                                    className="crm-input"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                    <button
                        className="crm-btn crm-btn-secondary"
                        onClick={refetch}
                        title="Refresh"
                    >
                        ⟳ Refresh
                    </button>
                    <button
                        className="crm-btn crm-btn-primary"
                        onClick={() => history.push(`${leadsPath()}/new`)}
                    >
                        + Add Lead
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="crm-stats-grid crm-dashboard-kpis">
                {[
                    {
                        label: 'Total Leads',
                        value: stats?.cards?.totalLeads || 0,
                        sub: `${stats?.cards?.totalLeads || 0} in range`,
                        icon: '◉',
                        onClick: () => history.push(leadsPath()),
                        series: sparklineValues(timeSeries.leads || []),
                    },
                    {
                        label: 'Qualified Leads',
                        value: stats?.cards?.qualifiedLeads || 0,
                        sub: `${qualifiedStage?.pct || 0}% in current funnel`,
                        icon: '◎',
                        onClick: () => history.push(`${leadsPath()}?status=qualified`),
                        series: sparklineValues(timeSeries.leads || []),
                    },
                    {
                        label: 'Lead Conversions',
                        value: convertedStage?.count || 0,
                        sub: `${convertedStage?.pct || 0}% overall`,
                        icon: '✓',
                        onClick: () => history.push(`${leadsPath()}?status=converted`),
                        series: sparklineValues(timeSeries.converted || []),
                    },
                    {
                        label: 'Pipeline Value',
                        value: formatCurrency(stats?.cards?.pipelineValue || 0),
                        sub: 'Open deals pipeline',
                        icon: '₹',
                        onClick: () => history.push(`${crmBasePath()}/deals`),
                        series: sparklineValues(timeSeries.leads || []),
                    },
                    {
                        label: 'Weighted Forecast',
                        value: formatCurrency(stats?.cards?.weightedForecast || 0),
                        sub: 'Weighted pipeline value',
                        icon: '◈',
                        onClick: () => history.push(`${crmBasePath()}/deals`),
                        series: sparklineValues(timeSeries.converted || []),
                    },
                    {
                        label: 'Follow-up Health',
                        value: stats?.cards?.followupHealth || 0,
                        sub: `Pending: ${stats?.activities?.pending || 0}`,
                        icon: '▤',
                        onClick: () => history.push(`${activitiesPath()}?status=pending`),
                        series: sparklineValues(timeSeries.conversations || timeSeries.activitiesCompleted || []),
                    },
                    {
                        label: 'Conversations Logged',
                        value: stats?.cards?.conversations || 0,
                        sub: 'Calls, meetings, notes in range',
                        icon: 'C',
                        tone: 'purple',
                        onClick: () => history.push(activitiesPath()),
                        series: sparklineValues(timeSeries.activitiesCompleted || []),
                    },
                    {
                        label: 'Open Deals',
                        value: stats?.deals?.open || 0,
                        sub: `${stats?.deals?.total || 0} total deals`,
                        icon: 'D',
                        tone: 'blue',
                        onClick: () => history.push(`${crmBasePath()}/deals`),
                        series: sparklineValues(timeSeries.leads || []),
                    },
                    {
                        label: 'Win Rate',
                        value: `${stats?.deals?.winRate || 0}%`,
                        sub: `${stats?.deals?.won || 0} won / ${closedDealsCount} closed`,
                        icon: '%',
                        tone: 'green',
                        onClick: () => history.push(`${crmBasePath()}/deals`),
                        series: sparklineValues(timeSeries.converted || []),
                    },
                    {
                        label: 'Overdue Follow-ups',
                        value: stats?.activities?.overdue || 0,
                        sub: `${stats?.activities?.dueToday || 0} due today`,
                        icon: '!',
                        tone: 'red',
                        onClick: () => history.push(`${activitiesPath()}?status=overdue`),
                        series: sparklineValues(timeSeries.activitiesCompleted || []),
                    },
                ].map((card) => (
                    <div key={card.label} className="crm-stat-card" onClick={card.onClick} style={{ cursor: 'pointer' }}>
                        <div className={`icon ${card.tone || 'primary'}`}>{card.icon}</div>
                        <div className="label">{card.label}</div>
                        <div className="value">{card.value}</div>
                        <div className="change">{card.sub}</div>
                    </div>
                ))}
            </div>

            {/* Two Column Layout */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                marginTop: '24px'
            }}>
                {/* Lead Funnel */}
                <div className="crm-card">
                    <div className="crm-card-header">
                        <h2 className="crm-card-title">Lead Funnel</h2>
                        <button className="crm-btn crm-btn-link" onClick={() => history.push(leadsPath())}>View All →</button>
                    </div>
                    <div className="crm-card-body">
                        {leadFunnel.map((step) => {
                            const label = step.stage?.charAt(0).toUpperCase() + step.stage?.slice(1);
                            const percent = step.pct || 0;
                            return (
                                <div key={step.stage} className="crm-funnel-row" onClick={() => history.push(`${leadsPath()}?status=${step.stage}`)}>
                                    <div className="crm-funnel-label">
                                        <span>{label}</span>
                                        <strong>{step.count || 0}</strong>
                                    </div>
                                    <div className="crm-funnel-bar">
                                        <span style={{ width: `${percent}%` }} />
                                    </div>
                                    <div className="crm-funnel-dropoff">{percent}%</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Source Performance */}
                <div className="crm-card">
                    <div className="crm-card-header">
                        <h2 className="crm-card-title">Source Performance</h2>
                    </div>
                    <div className="crm-card-body">
                        <table className="crm-table">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Leads</th>
                                    <th>Lead Share</th>
                                    <th>Qualified</th>
                                    <th>Converted</th>
                                    <th>Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sourcePerformance.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                            No source data yet.
                                        </td>
                                    </tr>
                                ) : (
                                    sourcePerformance.map((row) => {
                                        const rate = Number.isFinite(row?.rate)
                                            ? row.rate
                                            : (Number.isFinite(row?.conversionRate) ? row.conversionRate : 0);
                                        const share = totalSourceLeads > 0
                                            ? Math.round(((Number(row?.leads) || 0) / totalSourceLeads) * 100)
                                            : 0;
                                        return (
                                            <tr key={row.source} onClick={() => history.push(`${leadsPath()}?source=${row.source}`)} style={{ cursor: 'pointer' }}>
                                                <td>{row.source}</td>
                                                <td>{row.leads}</td>
                                                <td>{share}%</td>
                                                <td>{row.qualified}</td>
                                                <td>{row.converted}</td>
                                                <td>{rate}%</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default CRMDashboard;
